import { store } from '@src/store';
import { DataPoint, DataPointWithValue } from '@src/types/DataPoint';
import { IMqttMessage } from '@src/types/Mqtt';
import { MqttClient } from 'mqtt';
import { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react';

const EMPTY_DATA_POINTS_WITH_VALUES: Array<DataPointWithValue> = [];

function updateDataPointsWithValues(
  prev: Array<DataPointWithValue>,
  topic: string,
  dataPointWithValue: DataPointWithValue,
): Array<DataPointWithValue> {
  // initial state fetch hook is undefined, therefore prev is first an [], and we need to initialize it. This sets the order of the points
  const index = prev.findIndex((p) => p.fullTopic === topic);

  if (index === -1) {
    return prev;
  }

  return prev.map((p, i) => (i === index ? dataPointWithValue : p));
}

function handleBadPayload(topic: string, e: string): IMqttMessage {
  console.error(`Error parsing message for topic : ${topic}`, e);
  return { value: null, ts: null };
}

export function parsePayload(message: Buffer, topic: string): IMqttMessage {
  try {
    const payload = JSON.parse(message.toString());
    if (
      payload.value === null ||
      payload.ts === null ||
      payload.value === undefined ||
      payload.ts === undefined
    ) {
      return handleBadPayload(topic, "Payload doesn't contain value or ts");
    }
    return payload;
  } catch (e) {
    return handleBadPayload(topic, String(e));
  }
}

function messageHandler(
  topic: string,
  message: Buffer,
  dataPoints: Array<DataPoint>,
  setDataPointsWithValues: Dispatch<SetStateAction<Array<DataPointWithValue>>>,
) {
  const { value, ts } = parsePayload(message, topic);
  const [dataPoint] = dataPoints.filter((s) => s.fullTopic === topic);

  if (!dataPoint) {
    return;
  }

  const dataPointWithValue: DataPointWithValue = {
    ...dataPoint,
    value,
    ts,
  };
  setDataPointsWithValues((prev) =>
    updateDataPointsWithValues(prev, topic, dataPointWithValue),
  );
}
function useSubscription(
  dataPoints: DataPoint[],
  useNewClient?: MqttClient,
): Array<DataPointWithValue> {
  const initialDataPointsWithValues = useMemo(
    () =>
      dataPoints.length === 0
        ? EMPTY_DATA_POINTS_WITH_VALUES
        : dataPoints.map((dp) => ({ ...dp, value: undefined, ts: undefined })),
    [dataPoints],
  );

  const [dataPointsWithValues, setDataPointsWithValues] = useState<
    Array<DataPointWithValue>
  >(initialDataPointsWithValues);

  // Initializes data load when available from fetch query
  useEffect(() => {
    setDataPointsWithValues(initialDataPointsWithValues);
  }, [initialDataPointsWithValues]);

  useEffect(() => {
    if (dataPoints.length === 0) {
      return;
    }
    const state = store.getState();
    const mqttClient =
      useNewClient !== undefined ? useNewClient : state.mqtt.clientMQTT;
    const topics = dataPoints
      .filter((t) => t?.fullTopic)
      .map((t) => t.fullTopic);
    const onMessageCb = (topic: string, buffer: Buffer) => {
      if (topics.includes(topic)) {
        messageHandler(topic, buffer, dataPoints, setDataPointsWithValues);
      }
    };
    if (mqttClient) {
      mqttClient.on('message', onMessageCb);
      mqttClient.subscribe(topics);
    }
    return () => {
      if (mqttClient) {
        mqttClient.off('message', onMessageCb);
        setDataPointsWithValues([]);
        // Defer unsubscribe to not block route transitions
        queueMicrotask(() => {
          if (mqttClient.connected && !mqttClient.disconnecting) {
            mqttClient.unsubscribe(topics);
          }
        });
      }
    };
  }, [dataPoints, useNewClient]);
  return dataPointsWithValues;
}

export default useSubscription;
